import {Form as JsonSchemaForm} from "@rjsf/bootstrap-4";
import moment from "moment";
import _ from 'lodash';
import React, {useEffect, useRef, useState} from "react";
import BlockUi from 'react-block-ui';
import 'react-block-ui/style.css';
import {Button, Form, Modal} from "react-bootstrap";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router";
import {useDocumentTitle} from "../app/hooks/useDocumentTitle";
import AlertDialog from "../dialog/AlertDialog";
import ArrayFieldComponent from "./ArrayFieldComponent";
import CheckboxComponent from "./CheckboxComponent";
import DateField from "./DateField";
import DatePickerComponent from "./DatePickerComponent";
import DiagnosticStatement from "./DiagnosticStatement";
import FlexiGridCheckboxComponent from "./FlexGridCheckboxComponent";
import {
	createNote,
	enableLiveValidation,
	getClientSupervisors,
	getSchema,
	saveData,
	updateFormData,
	updateUserAction
} from "./formSlice";
import RadioInputComponent from "./RadioInputComponent";
import RatingWidget from "./RatingWidget";
import SignatureComponent from "./SignatureComponent";
import SNDiagnosticCodeSelect from "./SNDiagnosticCodeSelect";
import SNTreatmentGoalSelect from "./SNTreatmentGoalSelect";
import StaticTextComponent from "./StaticTextComponent";
import TextareaComponent from "./TextareaComponent";
import TitleFieldComponent from "./TitleFieldComponent";
import TPGoalObjectives from "./TPGoalObjectives";
import TPLastDiagnosticAssessmentStmt from "./TPLastDiagnosticAssessmentStmt";
import TPLastTreatmentStmt from "./TPLastTreatmentStmt";
import TPTreatmentGoalObjectives from "./TPTreatmentGoalObjectives";
import TypeaheadField from "./TypeaheadField";

const LOGO_URL = "https://schedule.prnet.me/static/media/mindfully-logo.6ecc5f4cbb69f7dc2a56.png";

function JsonForm() {
	const dispatch = useDispatch();
	const [documentTitle, setDocumentTitle] = useDocumentTitle();
	const [clinicianId, setClinicianId] = useState("");
	const [showModal, setShowModal] = useState();
	const [keepInView, setKeepInView] = useState(false);

	// DOM reference
	let formContainer = useRef();
	let saveAndSendBtnRef = useRef();

	// url params
	const { token } = useParams();

	// redux state
	const {
		clientId,
		formData,
		formName,
		isLinkExpired,
		isLoading,
		isProfileEmpty,
		liveValidation,
		role,
		schema,
		status,
		uiSchema,
		userAction,
		validationEnabled,
		clinicians
	} = useSelector(state => state.form)

	useEffect(() => {
		setKeepInView(true);
	}, []);

	// get form schema
	useEffect(() => {
		dispatch(getSchema(token));
	}, [token])

	// get client details
	useEffect(() => {
		if (role === "clinician") {
			dispatch(getClientSupervisors(clientId))
		}
	}, [clientId])

	useEffect(() => {
		setDocumentTitle(formName);
	}, [formName]);

	function validateSignatureFields(formData, errors) {
		let { clinicianInfo, supervisorInfo } = formData;

		if (userAction === 'submit') {
			if (_.isEmpty(clinicianInfo.signature) && _.isEmpty(supervisorInfo.signature)) {
				errors.clinicianInfo.signature.addError("Signature is required.");
				errors.supervisorInfo.signature.addError("Signature is required.");
			}
		}
	}

	function validateCheckboxGroup(formData, errors) {
		let { required, properties } = schema;

		required.forEach(field => {
			let property = properties[field];
			let { type } = property;

			if (type === 'object') {
				let data = formData[field];
				let fields = Object.keys(property['properties'] || {});
				let hasErrors = true;

				if (!_.isEmpty(data) && _.isEmpty(fields)) {
					hasErrors = false;
				}

				if (!_.isEmpty(fields)) {
					for (let i in fields) {
						let fieldName = fields[i];

						if (!_.isUndefined(data[fieldName]) && data[fieldName] === true) {
							hasErrors = false;
							break;
						}
					}
				}

				if (hasErrors) {
					errors[field].addError("is a required field.");
				}
			}
		});
	}

	// handle form validation 
	function validate(formData, errors) {
		if (!liveValidation) {
			dispatch(enableLiveValidation());
		}

		validateCheckboxGroup(formData, errors);
		validateSignatureFields(formData, errors);

		return errors;
	}

	// handle form error
	function handleError(props) {
		setTimeout(() => {
			let errorElements = formContainer.current.getElementsByClassName('text-danger');

			if (errorElements.length) {
				errorElements[0].scrollIntoView(true);
			}
		}, 100);
	}

	function handleChange(props) {
		dispatch(updateFormData(props.formData))
	}

	/**
	 * Add note to clinician
	 */
	function addNote() {
		const { F1: firstName, F2: lastName } = formData['S1$G1'];

		let data = {
			title: "Form signature required",
			description: `You were requested to sign the ${formName} for ${lastName} ${firstName}. Please <a href="${document.location.href}" target="_blank"><strong>click here</strong></a> to sign and submit.`,
			assignees: [{ id: clinicianId }],
			category: 'FORM_ASSIGNMENT'
		}

		dispatch(createNote(data))
		setShowModal(false);
	}

	// handle form submit
	function handleSubmit(props, e) {
		let formData = props.formData;

		let payload = {
			action: userAction,
			formName,
			token,
			data: JSON.stringify(formData, (key, value) => {
				return typeof value === 'undefined' ? "" : value;
			})
		}

		if (userAction === 'save_and_send') {
			addNote();
		}

		dispatch(saveData(payload));
	}

	// transform errors
	function transformErrors(errors) {
		return errors.map(error => {
			if (error.name === "required") {
				error.message = "is a required field."
			}

			return error;
		})
	}

	function getAlertProps() {
		let props = {
			title: "Confirmation",
			message: "",
			onClose: null
		};

		if (isLinkExpired) {
			_.assign(props, {
				title: 'Error',
				message: 'This form link is no longer active. If you need to fill in your form, please contact our office at (888) 830-0347 during office hours to be sent a new link.',
			})
		} else if (status === 'SUBMITTED') {
			_.assign(props, {
				message: 'A PDF of this form has been saved.',
				onClose: () => window.close()
			})
		} else if (status === 'SAVED' || status === "NOT COMPLETED") {
			_.assign(props, {
				message: 'Your changes to this form have been saved.',
				onClose: () => window.close()
			})

			if (role === 'client') {
				_.assign(props, {
					message: 'Your form has been submitted.',
					onClose: () => window.location.href = "https://mindfully.com"
				})
			}
		} else if(status === 'UNAUTHORIZED') {
			_.assign(props, {
				message: 'Session expired, please save your work, close your window and sign back in through PRN',
				onClose: null
			})
		} else if (isProfileEmpty && !isLoading) {
			_.assign(props, {
				title: "",
				message: "No signature found."
			})
		}

		return props;
	}

	const handleClinicianChange = (e) => {
		setClinicianId(e.target.value);
	}

	const handleClose = () => setShowModal(false);
	const handleSaveAndSend = () => setShowModal(true);

	// Render form
	function renderForm() {

		const formNameSlug = formName.toLowerCase().replace(/\s/g, '');
		const formSchema = _.cloneDeep(schema);
		const formDataCustom = _.cloneDeep(formData);

		if (formNameSlug === 'individualsessionnote') {
			if(formSchema?.properties?.F243?.title) {
				formSchema.properties.F243.title = "Date of Session";
			}
			
			if(formDataCustom?.F243) {
				const date = moment(formData?.F243);
				const now = moment();
				if (date.isAfter(now)) {
					formDataCustom.F243 = "";
				}
			}
		}
		
		if(formNameSlug === 'treatmentplan') {
			if(formSchema?.properties?.S1$G1?.properties) {
				formSchema.properties.S1$G1.properties = {...formSchema?.properties?.S1$G1?.properties, ...{ F4 :{
					"title": "How often is the client to be seen?",
					"type": "number",
					"oneOf": [
						{
							"const": 2,
							"title": "Twice a week"
						},
						{
							"const": 1,
							"title": "Weekly"
						},
						{
							"const": 0.5,
							"title": "Twice monthly"
						},
						{
							"const": 0.25,
							"title": "Once monthly"
						},
						{
							"const":  0.01,
							"title": "Maintenance"
						},
						{
							"const": 0,
							"title": "Inactive"
						}
					],
					"default": 2
				}}};
			}
		}

		const fields = {
			TitleField: (props) => <TitleFieldComponent {...props} />,
			flexiGridCheckboxes: (props) => <FlexiGridCheckboxComponent {...props} />,
			TypeaheadField: (props) => <TypeaheadField {...props} />,
			DiagnosticStatement: (props) => <DiagnosticStatement {...props} />,
			TPGoalObjectives: (props) => <TPGoalObjectives {...props} />,
			TPTreatmentGoalObjectives: (props) => <TPTreatmentGoalObjectives {...props} />,
			TPLastTreatmentStmt: (props) => <TPLastTreatmentStmt {...props} />,
			TPLastDiagnosticAssessmentStmt: (props) => <TPLastDiagnosticAssessmentStmt {...props} />,
			DateField: (props) => <DateField {...props} />,
			SNDiagnosticCodeSelect: (props) => <SNDiagnosticCodeSelect {...props} />,
			SNTreatmentGoalSelect: (props) => <SNTreatmentGoalSelect {...props} />
		}

		const widgets = {
			RadioWidget: (props) => <RadioInputComponent {...props} />,
			CheckboxWidget: (props) => <CheckboxComponent {...props} />,
			TextareaWidget: (props) => <TextareaComponent {...props} />,
			DateWidget: (props) => <DatePickerComponent {...props} />,
			signature: (props) => <SignatureComponent {...props} />,
			staticText: (props) => <StaticTextComponent {...props} />,
			RatingWidget: (props) => <RatingWidget {...props} />
		}

		return (
			<React.Fragment>
				<div className="border-bottom mb-3 pb-3 text-center">
					<img src={LOGO_URL} alt="" className="prnf-cp-logo" />
				</div>
				<JsonSchemaForm
					schema={formSchema}
					uiSchema={uiSchema}
					formData={formDataCustom}
					fields={fields}
					widgets={widgets}
					ArrayFieldTemplate={ArrayFieldComponent}
					noHtml5Validate={true}
					showErrorList={false}
					transformErrors={transformErrors}
					validate={validate}
					onError={handleError}
					onSubmit={handleSubmit}
					onChange={handleChange}
					liveValidate={liveValidation}
					idPrefix="prn"
					noValidate={!validationEnabled}
				>
					<hr />
					{role === "clinician" &&
						<div className="form-group">
							<button type="submit" name="save" className="btn btn-success"
								onClick={() => dispatch(updateUserAction('save'))}>Save</button>
							<button type="button" className="btn btn-info"
								onClick={handleSaveAndSend}>Save and Send</button>&nbsp;&nbsp;
							<button type="submit" name="submit" className="btn btn-primary"
								onClick={() => dispatch(updateUserAction('submit'))}>Submit</button>
							<button ref={saveAndSendBtnRef} type="submit" className="hidden" onClick={() => dispatch(updateUserAction('save_and_send'))} />
						</div>
					}

					{role === "client" &&
						<div className="form-group">
							<button type="submit" name="save" className="btn btn-primary">Submit</button>
						</div>
					}

					<Modal
						show={showModal}
						onHide={handleClose}
						backdrop="static"
						keyboard={false}
					>
						<Modal.Header closeButton>
							<Modal.Title>Save and Send</Modal.Title>
						</Modal.Header>
						<Modal.Body>
							<Form.Group className="mb-3">
								<Form.Label>Select Clinician</Form.Label>
								<select className="form-control" onChange={handleClinicianChange} value={clinicianId}>
									<option value="">Select...</option>
									{
										clinicians.map((clinician, i) => <option key={i} value={clinician.personId}>{clinician.lastName} {clinician.firstName}</option>)
									}
								</select>
							</Form.Group>
						</Modal.Body>
						<Modal.Footer>
							<Button variant="secondary" onClick={handleClose}>
								Close
							</Button>
							<button className="btn btn-primary" disabled={_.isEmpty(clinicianId)}
								onClick={() => saveAndSendBtnRef.current.click()}>Save and Send</button>
						</Modal.Footer>
					</Modal>
				</JsonSchemaForm>
			</React.Fragment>
		)
	}

	return (
		<React.Fragment>
			<BlockUi tag="div" blocking={isLoading} message="Processing..." keepInView={keepInView}>
				<div className="prn-form" ref={formContainer}>
					<div className="container">
						<div className="row">
							<div className="col px-md-4">
								{!_.isEmpty(schema) && renderForm()}
							</div>
						</div>
					</div>
				</div>

				<AlertDialog {...getAlertProps()} />
			</BlockUi>
		</React.Fragment>
	)
}

export default JsonForm;